import { z } from 'zod';
import { courses, courseValues } from './courses';
import type { Course } from './courses';
import { heardFromSchema, manualLeadChannelSchema } from './leadChannels';

export const excursionValues = ['trolleybus_depot_1', 'eastern_tram_depot', 'western_tram_depot', 'city_tram', 'city_tram_custom', 'tram_karaoke', 'botanical_walk'] as const;
export type Excursion = (typeof excursionValues)[number];

export const excursionLabels: Record<Excursion, string> = {
  eastern_tram_depot: 'Восточное трамвайное депо',
  city_tram: 'Трамвай по городу',
  trolleybus_depot_1: 'Первое троллейбусное депо',
  western_tram_depot: 'Западное трамвайное депо',
  city_tram_custom: 'Трамвай по городу — нестандартный маршрут',
  tram_karaoke: 'Караоке на колёсах',
  botanical_walk: 'Ботаническая прогулка и сбор гербария',
};

/**
 * Everything a visitor can book: our excursions and the partner courses. A lead keeps its offer in the
 * `excursion` field/column; excursion-only data (facts, sessions, documents, counters) stays keyed by `Excursion`.
 */
export const offerValues = [...excursionValues, ...courseValues] as const;
export type Offer = (typeof offerValues)[number];
export const isCourse = (offer: Offer): offer is Course => (courseValues as readonly string[]).includes(offer);
export const isExcursion = (offer: Offer): offer is Excursion => (excursionValues as readonly string[]).includes(offer);
export const offerLabels = {
  ...excursionLabels,
  ...Object.fromEntries(courseValues.map((course) => [course, `Курс «Добро и Небо»: ${courses[course].title}`])),
} as Record<Offer, string>;
/** One line for staff chats and cards: «Экскурсия: …» or the course label, which names itself. */
export const offerLine = (offer: Offer): string => (isCourse(offer) ? offerLabels[offer] : `Экскурсия: ${offerLabels[offer]}`);

export const bookingFormatValues = ['group', 'individual'] as const;
export type BookingFormat = (typeof bookingFormatValues)[number];
export const bookingFormatLabels: Record<BookingFormat, string> = {
  group: 'Групповая экскурсия',
  individual: 'Индивидуальная экскурсия',
};
export type LegacyBookingFormat = 'private' | 'join_group';
export const legacyBookingFormatLabels: Record<LegacyBookingFormat, string> = {
  private: 'Своя компания (историческая заявка)',
  join_group: 'Присоединиться к сборной группе (историческая заявка)',
};
export const consentVersions = {
  personalData: '2026-10-08.1',
  media: '2026-10-08.1',
  terms: '2026-10-08.1',
} as const;

const normalizedText = (minimum: number, maximum: number) => z.string()
  .transform((value) => value.trim().replace(/\s+/g, ' '))
  .pipe(z.string().min(minimum, 'Заполните поле').max(maximum, `Максимум ${maximum} символов`));

/** Russian phone starting with `8` or `+7` (11 digits, separators allowed) or a messenger nick, optionally with a leading `@`. */
export function isPhoneOrNick(value: string): boolean {
  if (/^@?[a-zA-Z][\w.]{2,}$/.test(value)) return true;
  return /^(\+7|8)[\d\s()\-]+$/.test(value) && value.replace(/\D/g, '').length === 11;
}

/**
 * First-touch attribution of a public form: `utm_source`/`utm_content` of the URL the visitor landed on
 * in this tab (for tracked links `/go/<code>`, `content` is the link code). Not an identifier, so it is
 * sent regardless of cookie consent.
 */
export const refValuePattern = /^[A-Za-z0-9_.-]{1,60}$/;
export const refValueMaxLength = 60;
const refValue = z.string().regex(refValuePattern);
export const leadRefSchema = z.object({ source: refValue, content: refValue.optional() }).strict();
export type LeadRef = z.infer<typeof leadRefSchema>;
/** Optional `ref` of the public forms: malformed attribution is dropped, never blocks the submission. */
export const optionalRefSchema = leadRefSchema.optional().catch(undefined);

/** Strips characters outside `refValuePattern`; `undefined` when nothing is left. */
export function sanitizeRefValue(value: string | null | undefined): string | undefined {
  const cleaned = (value ?? '').trim().replace(/[^A-Za-z0-9_.-]/g, '').slice(0, refValueMaxLength);
  return cleaned || undefined;
}

/** Name, contact, comment and the consent shared by every lead form (also the Mini App call form). */
export const leadContact = {
  name: normalizedText(2, 100),
  contact: normalizedText(3, 160).refine(isPhoneOrNick, 'Телефон в формате +7… или 8… (11 цифр) либо ник в мессенджере'),
  comment: z.string().trim().max(1000, 'Максимум 1000 символов').optional().default(''),
  website: z.string().max(0).optional().default(''),
  privacyAccepted: z.literal(true, { errorMap: () => ({ message: 'Нужно согласие на обработку данных' }) }),
};
/** Excursions need the participation format; the park applies to the botanical walk only. */
const excursionLeadFields = z.object({
  ...leadContact,
  excursion: z.enum(excursionValues),
  bookingFormat: z.enum(bookingFormatValues, { errorMap: () => ({ message: 'Выберите формат участия' }) }),
  preferredPark: normalizedText(0, 200).optional().default(''),
});
/** Courses are booked by name and contact alone: excursion-only fields are rejected as unknown keys. */
const courseLeadFields = z.object({ ...leadContact, excursion: z.enum(courseValues) });
const offerError = { errorMap: () => ({ message: 'Выберите экскурсию или курс из списка' }) };
const parkOnlyForBotanical = (value: { excursion: Offer; preferredPark?: string }) => value.excursion === 'botanical_walk' || !value.preferredPark;
const parkOnlyForBotanicalIssue = { message: 'Парк можно выбрать только для ботанической прогулки', path: ['preferredPark'] };
const publicFields = { ref: optionalRefSchema };
/** Staff pick the channel; «откуда узнали» is optional. */
const staffFields = { channel: manualLeadChannelSchema, heardFrom: heardFromSchema.nullable().optional().default(null) };

/** Lead as staff create it (`POST /api/admin/leads`): the channel, no attribution. */
export const leadSchema = z.discriminatedUnion('excursion', [excursionLeadFields.extend(staffFields).strict(), courseLeadFields.extend(staffFields).strict()], offerError)
  .refine(parkOnlyForBotanical, parkOnlyForBotanicalIssue);
/** `POST /api/leads`: the public form adds the optional first-touch `ref`; its channel is always `site`. */
export const publicLeadSchema = z.discriminatedUnion('excursion', [excursionLeadFields.extend(publicFields).strict(), courseLeadFields.extend(publicFields).strict()], offerError)
  .refine(parkOnlyForBotanical, parkOnlyForBotanicalIssue);

export type LeadInput = z.input<typeof leadSchema>;
export type PublicLeadInput = z.input<typeof publicLeadSchema>;
/** What `createLead` stores from any form: excursion leads carry the format (and park), course leads neither. */
export type LeadDetails = { name: string; contact: string; excursion: Offer; comment: string; bookingFormat?: BookingFormat; preferredPark?: string };

export type ApiError = {
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
  };
};
